import { directive, Directive } from 'lit/directive.js';
import { LitElement, css, html } from 'lit';
import { state, query, property, customElement } from 'lit/decorators.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const ARIA_IDREF_ATTRIBUTES = new Map([
    ['aria-activedescendant', 'ariaActivedescendantElement'],
    ['aria-controls', 'ariaControlsElements'],
    ['aria-describedby', 'ariaDescribedByElements'],
    ['aria-details', 'ariaDetailsElements'],
    ['aria-errormessage', 'ariaErrormessageElements'],
    ['aria-flowto', 'ariaFlowtoElements'],
    ['aria-labelledby', 'ariaLabelledByElements'],
    ['aria-owns', 'ariaOwnsElements'],
]);
function resolveAriaIdRefs(element, value) {
    const ids = value
        .trim()
        .split(/\s+/)
        .filter((id) => id.length > 0);
    if (ids.length === 0)
        return null;
    const root = element.getRootNode();
    let resolvedElements = [];
    if (root instanceof ShadowRoot) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else if (root instanceof Document) {
        resolvedElements = ids.map((id) => root.getElementById(id));
    }
    else {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    if (resolvedElements.filter((el) => el !== null).length === 0) {
        resolvedElements = ids.map((id) => document.getElementById(id));
    }
    const validElements = resolvedElements.filter((el) => el !== null);
    return validElements.length === ids.length ? validElements : null;
}
function setInternalsProperty(el, name, resolvedElements) {
    if (name === 'aria-activedescendant') {
        const [firstElement] = resolvedElements;
        // eslint-disable-next-line no-param-reassign
        el[name] = firstElement;
    }
    else {
        // eslint-disable-next-line no-param-reassign
        el[name] = resolvedElements;
    }
}
class SetAttributesDirective extends Directive {
    update(part, [attributes]) {
        const el = part.element;
        const internals = el.internals ?? null;
        for (const [name, value] of Object.entries(attributes)) {
            if (value == null) {
                el.removeAttribute(name);
            }
            else {
                const internalsPropertyName = ARIA_IDREF_ATTRIBUTES.get(name);
                if (internalsPropertyName) {
                    const resolvedElements = resolveAriaIdRefs(el, String(value));
                    if (resolvedElements) {
                        if (internals) {
                            // Set on ElementInternals
                            setInternalsProperty(internals, internalsPropertyName, resolvedElements);
                        }
                        else {
                            // Set internals property on shadowdom item
                            el.setAttribute(name, String(value));
                            setInternalsProperty(el, internalsPropertyName, resolvedElements);
                        }
                    }
                }
                else {
                    // Non-ARIA IDREF attributes: set normally
                    el.setAttribute(name, String(value));
                }
            }
        }
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(_attributes) {
        return null;
    }
}
/* How to use:

<textarea ${setAttributes(this._ariaAttributes)} ... />
*/
directive(SetAttributesDirective);

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e)){var o=e.length;for(t=0;t<o;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);}else for(f in e)e[f]&&(n&&(n+=" "),n+=f);return n}function clsx(){for(var e,t,f=0,n="",o=arguments.length;f<o;f++)(e=arguments[f])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

const connectedElements = new Set();
if (window && !window.translations) {
    window.translations = new Map();
}
const { translations } = window;
let fallback;
// TODO: We need some way for users to be able to set these on the server.
let documentDirection = 'ltr';
// Fallback for server.
let documentLanguage = 'en';
const isClient = typeof MutationObserver !== 'undefined' &&
    typeof document !== 'undefined' &&
    typeof document.documentElement !== 'undefined';
/** Updates all localized elements that are currently connected */
function update() {
    if (isClient) {
        documentDirection = document.documentElement.dir || 'ltr';
        documentLanguage = document.documentElement.lang || navigator.language;
    }
    [...connectedElements.keys()].forEach((el) => {
        const litEl = el;
        if (typeof litEl.requestUpdate === 'function') {
            litEl.requestUpdate();
        }
    });
}
if (isClient) {
    const documentElementObserver = new MutationObserver(update);
    documentDirection = document.documentElement.dir || 'ltr';
    documentLanguage = document.documentElement.lang || navigator.language;
    // Watch for changes on <html lang>
    documentElementObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['dir', 'lang'],
    });
}
/** Registers one or more translations */
function registerTranslation(...translation) {
    translation.forEach((t) => {
        const code = t.$code.toLowerCase();
        if (translations.has(code)) {
            // Merge translations that share the same language code
            translations.set(code, { ...translations.get(code), ...t });
        }
        else {
            translations.set(code, t);
        }
        // The first translation that's registered is the fallback
        if (!fallback) {
            fallback = t;
        }
    });
    update();
}
if (typeof window !== 'undefined') {
    window.registerTranslation = registerTranslation;
}
/**
 * Localize Reactive Controller for components built with Lit
 *
 * To use this controller, import the class and instantiate it in a custom element constructor:
 *
 * private localize = new LocalizeController(this);
 *
 * This will add the element to the set and make it respond to changes to <html dir|lang> automatically. To make it
 * respond to changes to its own dir|lang properties, make it a property:
 *
 *   @property() dir: string;
 *   @property() lang: string;
 *
 * To use a translation method, call it like this:
 *
 *   ${this.localize.term('term_key_here')}
 *   ${this.localize.date('2021-12-03')}
 *   ${this.localize.number(1000000)}
 */
class LocalizeController {
    constructor(host) {
        this.host = host;
        this.host.addController(this);
    }
    hostConnected() {
        connectedElements.add(this.host);
    }
    hostDisconnected() {
        connectedElements.delete(this.host);
    }
    /**
     * Gets the host element's directionality as determined by the `dir` attribute. The return value is transformed to
     * lowercase.
     */
    dir() {
        return `${this.host.dir || documentDirection}`.toLowerCase();
    }
    /**
     * Gets the host element's language as determined by the `lang` attribute. The return value is transformed to
     * lowercase.
     */
    lang() {
        return `${this.host.lang || documentLanguage}`.toLowerCase();
    }
    // eslint-disable-next-line class-methods-use-this
    getTranslationData(lang) {
        // Convert "en_US" to "en-US". Note that both underscores and dashes are allowed per spec, but underscores result in
        // a RangeError by the call to `new Intl.Locale()`. See: https://unicode.org/reports/tr35/#unicode-locale-identifier
        const locale = new Intl.Locale(lang.replace(/_/g, '-'));
        const language = locale?.language.toLowerCase();
        const region = locale?.region?.toLowerCase() ?? '';
        const primary = translations.get(`${language}-${region}`);
        const secondary = translations.get(language);
        return { locale, language, region, primary, secondary };
    }
    /** Determines if the specified term exists, optionally checking the fallback translation. */
    exists(key, options) {
        const { primary, secondary } = this.getTranslationData(options.lang ?? this.lang());
        const mergedOptions = {
            includeFallback: false,
            ...options,
        };
        if ((primary && primary[key]) ||
            (secondary && secondary[key]) ||
            (mergedOptions.includeFallback && fallback && fallback[key])) {
            return true;
        }
        return false;
    }
    /** Outputs a translated term. */
    term(key, ...args) {
        const { primary, secondary } = this.getTranslationData(this.lang());
        let term;
        // Look for a matching term using regionCode, code, then the fallback
        if (primary && primary[key]) {
            term = primary[key];
        }
        else if (secondary && secondary[key]) {
            term = secondary[key];
        }
        else if (fallback && fallback[key]) {
            term = fallback[key];
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`No translation found for: ${String(key)}`);
            return String(key);
        }
        if (typeof term === 'function') {
            return term(...args);
        }
        return term;
    }
    /** Outputs a localized date in the specified format. */
    date(dateToFormat, options) {
        const date = new Date(dateToFormat);
        return new Intl.DateTimeFormat(this.lang(), options).format(date);
    }
    /** Outputs a localized number in the specified format. */
    number(numberToFormat, options) {
        const num = Number(numberToFormat);
        return Number.isNaN(num) ? '' : new Intl.NumberFormat(this.lang(), options).format(num);
    }
    /** Outputs a localized time in relative format. */
    relativeTime(value, unit, options) {
        return new Intl.RelativeTimeFormat(this.lang(), options).format(value, unit);
    }
}

/**
 * @param Base The base class.
 * @returns A mix-in implementing `localizations` method.
 *
 *@example
 *    <!-- Terms -->
      ${this.$localize.term('hello')}
      or
      ${this.$t('hello')}

      <!-- Dates -->
      ${this.$localize.date('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}
      or
      ${this.$d('2021-09-15 14:00:00 ET', { month: 'long', day: 'numeric', year: 'numeric' })}

      <!-- Numbers/currency -->
      ${this.$localize.number(1000, { style: 'currency', currency: 'USD'})}
      or
      ${this.$n(1000,{ style: 'currency', currency: 'USD'})}

      <!-- Determining language -->
      ${this.$localize.lang()}

      <!-- Determining directionality, e.g. 'ltr' or 'rtl' -->
      ${this.$localize.dir()}


    *** HOW TO DEFINE TRANSLATIONS: ***
    // Simple terms
    upload: 'Upload',

    // Terms with placeholders
    greetUser: (name: string) => `Hello, ${name}!`,

    // Plurals
    numFilesSelected: (count: number) => {
        if (count === 0) return 'No files selected';
        if (count === 1) return '1 file selected';
        return `${count} files selected`;
    }
 */
const LocalizeMixin = (Base) => class extends Base {
    constructor() {
        super(...arguments);
        this.localize = new LocalizeController(this);
    }
    // Provide default values to avoid definite assignment errors and avoid decorators
    // commentati perchè danno problemi su React.js. Sono attributi nativi, e assegnare un valore di default a react da fastidio
    // dir: string = '';
    // lang: string = '';
    /**
     * Restituisce tutta l'utility di traduzione
     *

     *
     * @returns tutta l'utility di traduzione
     *
     * @example
     * this.$localize.lang() -> ritorna la lingua corrente
     * this.$localize.dir() -> ritorna la direzione della lingua corrente
     */
    get $localize() {
        return this.localize;
    }
    /**
     * Restituisce una stringa localizzata a partire da una chiave di termine.
     *
     * Utilizza il `LocalizeController` per accedere al dizionario corrente e
     * tradurre la chiave fornita secondo la lingua attiva.
     *
     * @param t - La chiave del termine da localizzare (es. 'hello', 'submit', ecc.).
     * @returns La stringa tradotta in base alla lingua attiva. Se la chiave non è trovata, restituisce la chiave stessa.
     *
     * @example
     * this.$t('hello'); // → "Ciao" (in locale it-IT)
     */
    $t(t) {
        // format term
        return this.localize.term(t);
    }
    /**
     * Formatta una data in base alla localizzazione attiva.
     *
     * Utilizza il `LocalizeController` per restituire una stringa localizzata
     * secondo le opzioni fornite (es. mese esteso, anno, ecc.).
     *
     * @param n - La data da formattare come stringa compatibile (es. ISO o con timezone, es. '2021-09-15 14:00:00 ET').
     * @param p - Le opzioni di formattazione per `Intl.DateTimeFormat` (es. { year: 'numeric', month: 'long', day: 'numeric' }).
     * @returns Una stringa rappresentante la data formattata secondo la localizzazione attiva.
     *
     * @example
     * this.$d('2021-09-15 14:00:00 ET', { year: 'numeric', month: 'long', day: 'numeric' });
     * // → "15 settembre 2021" (in locale it-IT)
     */
    $d(d, p) {
        // format date
        return this.localize.date(d, p);
    }
    /**
     * Formatta un numero secondo le impostazioni locali dell'utente corrente.
     *
     * Utilizza il `LocalizeController` per applicare formattazione numerica,
     * incluse opzioni come separatori, decimali, valute, ecc.
     *
     * @param d - Il numero da formattare.
     * @param p - Le opzioni di formattazione (es. { style: 'currency', currency: 'EUR' }).
     * @returns Una stringa rappresentante il numero formattato secondo la localizzazione attiva.
     *
     * @example
     * this.$n(1234.56, { style: 'currency', currency: 'USD' }); // → "$1,234.56" (in locale en-US)
     */
    $n(d, p) {
        return this.localize.number(d, p);
    }
};

/* eslint-disable no-console */
class Logger {
    constructor(tag) {
        this.tag = tag;
    }
    format(level, msg) {
        return [`[${this.tag}] ${msg}`];
    }
    warn(msg) {
        console.warn(...this.format('warn', msg));
    }
    error(msg) {
        console.error(...this.format('error', msg));
    }
    info(msg) {
        console.info(...this.format('info', msg));
    }
}

/**
 * Factory function per creare una base class estendibile
 * con stili personalizzati.
 */
class BaseComponent extends LitElement {
    constructor() {
        super();
        this.composeClass = clsx;
        this.logger = new Logger(this.tagName.toLowerCase());
    }
    get _ariaAttributes() {
        const attributes = {};
        for (const attr of this.getAttributeNames()) {
            if (attr === 'it-role') {
                attributes.role = this.getAttribute(attr);
            }
            else if (attr.startsWith('it-aria-')) {
                attributes[attr.replace(/^it-/, '')] = this.getAttribute(attr);
            }
        }
        return attributes;
    }
    // eslint-disable-next-line class-methods-use-this
    generateId(prefix) {
        return `${prefix}-${Math.random().toString(36).slice(2)}`;
    }
    // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
    addFocus(element) {
        // new TrackFocus(element); // per il momento è stato disattivato perchè ci sono le pseudo classi ::focus-visible per fare quello che fa TrackFocus. Si possono aggiungere regole css in bsi-italia 3 dato che stiamo facendo una breaking release di bsi.
    }
    // eslint-disable-next-line class-methods-use-this
    getActiveElement() {
        let active = document.activeElement;
        while (active && active.shadowRoot && active.shadowRoot.activeElement) {
            active = active.shadowRoot.activeElement;
        }
        return active;
    }
    get focusElement() {
        return this;
    }
    // eslint-disable-next-line class-methods-use-this
    get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    connectedCallback() {
        super.connectedCallback();
        // generate internal _id
        const prefix = this.id?.length > 0 ? this.id : this.tagName.toLowerCase();
        this._id = this.generateId(prefix);
        this.ariaAttributesObserver = new MutationObserver((mutations) => {
            const shouldUpdate = mutations.some((mutation) => {
                if (mutation.type !== 'attributes' || !mutation.attributeName) {
                    return false;
                }
                return mutation.attributeName === 'it-role' || mutation.attributeName.startsWith('it-aria-');
            });
            if (shouldUpdate) {
                this.requestUpdate();
            }
        });
        this.ariaAttributesObserver.observe(this, {
            attributes: true,
            attributeOldValue: true,
        });
    }
    disconnectedCallback() {
        this.ariaAttributesObserver?.disconnect();
        this.ariaAttributesObserver = undefined;
        super.disconnectedCallback();
    }
}
const BaseLocalizedComponent = LocalizeMixin(BaseComponent);

//
// We store a WeakMap of forms + controls so we can keep references to all FormControl within a given form. As
// elements connect and disconnect to/from the DOM, their containing form is used as the key and the form control is
// added and removed from the form's set, respectively.
//
const formCollections = new WeakMap();
//
// We store a WeakMap of reportValidity() overloads so we can override it when form controls connect to the DOM and
// restore the original behavior when they disconnect.
//
const reportValidityOverloads = new WeakMap();
const checkValidityOverloads = new WeakMap();
//
// We store a Set of controls that users have interacted with. This allows us to determine the interaction state
// without littering the DOM with additional data attributes.
//
const userInteractedControls = new WeakSet();
//
// We store a WeakMap of interactions for each form control so we can track when all conditions are met for validation.
//
const interactions = new WeakMap();
/** A reactive controller to allow form controls to participate in form submission, validation, etc. */
class FormControlController {
    constructor(host, options) {
        this.submittedOnce = false;
        this.handleFormData = (event) => {
            // console.log('handleFormData');
            const disabled = this.options.disabled(this.host);
            const name = this.options.name(this.host);
            const value = this.options.value(this.host);
            const tagName = this.host.tagName.toLowerCase();
            // For buttons, we only submit the value if they were the submitter. This is currently done in doAction() by
            // injecting the name/value on a temporary button, so we can just skip them here.
            const isButton = tagName === 'it-button';
            if (this.host.isConnected &&
                !disabled &&
                !isButton &&
                typeof name === 'string' &&
                name.length > 0 &&
                typeof value !== 'undefined') {
                switch (tagName) {
                    case 'it-radio':
                        if (this.host.checked) {
                            event.formData.append(name, value);
                        }
                        break;
                    case 'it-checkbox':
                    case 'it-toggle':
                        if (this.host.checked) {
                            if (event.formData.getAll(name).indexOf(value) < 0) {
                                // handle group checkbox
                                event.formData.append(name, value);
                            }
                        }
                        break;
                    case 'it-checkbox-group':
                    case 'it-toggle-group':
                        // non settare valori in formData, perchè ogni singola checkbox setta il suo valore
                        break;
                    case 'it-upload':
                        // value is File[] — append each File object directly (not as string)
                        if (Array.isArray(value)) {
                            value.forEach((file) => {
                                event.formData.append(name, file);
                            });
                        }
                        break;
                    default:
                        if (Array.isArray(value)) {
                            value.forEach((val) => {
                                event.formData.append(name, val.toString());
                            });
                        }
                        else {
                            event.formData.append(name, value.toString());
                        }
                }
            }
        };
        this.handleFormSubmit = (event) => {
            const disabled = this.options.disabled(this.host);
            const reportValidity = this.options.reportValidity;
            // Update the interacted state for all controls when the form is submitted
            if (this.form && !this.form.noValidate) {
                formCollections.get(this.form)?.forEach((control) => {
                    this.setUserInteracted(control, true);
                });
            }
            const resReportValidity = reportValidity(this.host);
            if (this.form && !this.form.noValidate && !disabled && !resReportValidity) {
                event.preventDefault();
                // event.stopImmediatePropagation(); // se lo attiviamo, valida un campo alla volta
                // Scroll al primo controllo non valido
                const formControls = formCollections.get(this.form);
                if (formControls) {
                    for (const control of formControls) {
                        if (!control.validity?.valid) {
                            // Scroll smooth verso il controllo non valido
                            control.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                            break;
                        }
                    }
                }
            }
            this.submittedOnce = true;
        };
        this.handleFormReset = () => {
            this.options.setValue(this.host, '');
            this.submittedOnce = false;
            this.setUserInteracted(this.host, false);
            interactions.set(this.host, []);
        };
        this.handleInteraction = (event) => {
            const emittedEvents = interactions.get(this.host);
            if (!emittedEvents.includes(event.type)) {
                emittedEvents.push(event.type);
            }
            // Mark it as user-interacted as soon as all associated events have been emitted
            if (emittedEvents.length === this.options.assumeInteractionOn.length) {
                this.setUserInteracted(this.host, true);
            }
        };
        this.checkFormValidity = () => {
            // console.log('checkFormValidity');
            //
            // This is very similar to the `reportFormValidity` function, but it does not trigger native constraint validation
            // Allow the user to simply check if the form is valid and handling validity in their own way.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.checkValidity === 'function') {
                        if (!element.checkValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        this.reportFormValidity = () => {
            // console.log('reportFormValidity');
            //
            // FormControl work hard to act like regular form controls. They support the Constraint Validation API
            // and its associated methods such as setCustomValidity() and reportValidity(). However, the HTMLFormElement also
            // has a reportValidity() method that will trigger validation on all child controls. Since we're not yet using
            // ElementInternals, we need to overload this method so it looks for any element with the reportValidity() method.
            //
            // We preserve the original method in a WeakMap, but we don't call it from the overload because that would trigger
            // validations in an unexpected order. When the element disconnects, we revert to the original behavior. This won't
            // be necessary once we can use ElementInternals.
            //
            // Note that we're also honoring the form's novalidate attribute.
            //
            if (this.form && !this.form.noValidate) {
                // This seems sloppy, but checking all elements will cover native inputs, Shoelace inputs, and other custom
                // elements that support the constraint validation API.
                const elements = this.form.querySelectorAll('*');
                for (const element of elements) {
                    if (typeof element.reportValidity === 'function') {
                        if (!element.reportValidity()) {
                            return false;
                        }
                    }
                }
            }
            return true;
        };
        (this.host = host).addController(this);
        this.options = {
            form: (input) => {
                // If there's a form attribute, use it to find the target form by id
                // Controls may not always reflect the 'form' property. For example, `<it-button>` doesn't reflect.
                const formId = input.form;
                if (formId) {
                    const root = input.getRootNode();
                    const form = root.querySelector(`#${formId}`);
                    if (form) {
                        return form;
                    }
                }
                return input.closest('form');
            },
            name: (input) => input.name,
            value: (input) => input.value,
            disabled: (input) => input.disabled ?? false,
            reportValidity: (input) => typeof input.reportValidity === 'function' ? input.reportValidity() : true,
            checkValidity: (input) => (typeof input.checkValidity === 'function' ? input.checkValidity() : true),
            setValue: (input, value) => {
                // eslint-disable-next-line no-param-reassign
                input.value = value;
            },
            assumeInteractionOn: ['it-input'],
            ...options,
        };
    }
    hostConnected() {
        const form = this.options.form(this.host);
        if (form) {
            this.attachForm(form);
        }
        // Listen for interactions
        interactions.set(this.host, []);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.addEventListener(event, this.handleInteraction);
        });
    }
    hostDisconnected() {
        this.detachForm();
        // Clean up interactions
        interactions.delete(this.host);
        this.options.assumeInteractionOn.forEach((event) => {
            this.host.removeEventListener(event, this.handleInteraction);
        });
    }
    hostUpdated() {
        const form = this.options.form(this.host);
        // Detach if the form no longer exists
        if (!form) {
            this.detachForm();
        }
        // If the form has changed, reattach it
        if (form && this.form !== form) {
            this.detachForm();
            this.attachForm(form);
        }
        if (this.host.hasUpdated) {
            this.setValidity(this.host.validity.valid);
        }
    }
    attachForm(form) {
        if (form) {
            this.form = form;
            // Add this element to the form's collection
            if (formCollections.has(this.form)) {
                formCollections.get(this.form).add(this.host);
            }
            else {
                formCollections.set(this.form, new Set([this.host]));
            }
            this.form.addEventListener('formdata', this.handleFormData);
            this.form.addEventListener('submit', this.handleFormSubmit);
            this.form.addEventListener('reset', this.handleFormReset);
            // Overload the form's reportValidity() method so it looks at FormControl
            if (!reportValidityOverloads.has(this.form)) {
                reportValidityOverloads.set(this.form, this.form.reportValidity);
                this.form.reportValidity = () => this.reportFormValidity();
            }
            // Overload the form's checkValidity() method so it looks at FormControl
            if (!checkValidityOverloads.has(this.form)) {
                checkValidityOverloads.set(this.form, this.form.checkValidity);
                this.form.checkValidity = () => this.checkFormValidity();
            }
        }
        else {
            this.form = undefined;
        }
    }
    detachForm() {
        if (!this.form)
            return;
        const formCollection = formCollections.get(this.form);
        if (!formCollection) {
            return;
        }
        this.submittedOnce = false;
        // Remove this host from the form's collection
        formCollection.delete(this.host);
        // Check to make sure there's no other form controls in the collection. If we do this
        // without checking if any other controls are still in the collection, then we will wipe out the
        // validity checks for all other elements.
        // see: https://github.com/shoelace-style/shoelace/issues/1703
        if (formCollection.size <= 0) {
            this.form.removeEventListener('formdata', this.handleFormData);
            this.form.removeEventListener('submit', this.handleFormSubmit);
            this.form.removeEventListener('reset', this.handleFormReset);
            // Remove the overload and restore the original method
            if (reportValidityOverloads.has(this.form)) {
                this.form.reportValidity = reportValidityOverloads.get(this.form);
                reportValidityOverloads.delete(this.form);
            }
            if (checkValidityOverloads.has(this.form)) {
                this.form.checkValidity = checkValidityOverloads.get(this.form);
                checkValidityOverloads.delete(this.form);
            }
            // So it looks weird here to not always set the form to undefined. But I _think_ if we unattach this.form here,
            // we end up in this fun spot where future validity checks don't have a reference to the form validity handler.
            // First form element in sets the validity handler. So we can't clean up `this.form` until there are no other form elements in the form.
            this.form = undefined;
        }
    }
    // eslint-disable-next-line class-methods-use-this
    setUserInteracted(el, hasInteracted) {
        if (hasInteracted) {
            userInteractedControls.add(el);
        }
        else {
            userInteractedControls.delete(el);
        }
        el.requestUpdate();
    }
    doAction(type, submitter) {
        // console.log('doaction', type);
        if (this.form) {
            const button = document.createElement('button');
            button.type = type;
            button.style.position = 'absolute';
            button.style.width = '0';
            button.style.height = '0';
            button.style.clipPath = 'inset(50%)';
            button.style.overflow = 'hidden';
            button.style.whiteSpace = 'nowrap';
            // Pass name, value, and form attributes through to the temporary button
            if (submitter) {
                button.name = submitter.name;
                button.value = submitter.value;
                ['formaction', 'formenctype', 'formmethod', 'formnovalidate', 'formtarget'].forEach((attr) => {
                    if (submitter.hasAttribute(attr)) {
                        button.setAttribute(attr, submitter.getAttribute(attr));
                    }
                });
            }
            this.form.append(button);
            button.click();
            button.remove();
        }
    }
    /** Returns the associated `<form>` element, if one exists. */
    getForm() {
        return this.form ?? null;
    }
    /** Resets the form, restoring all the control to their default value */
    reset(submitter) {
        this.doAction('reset', submitter);
    }
    /** Submits the form, triggering validation and form data injection. */
    submit(submitter) {
        // Calling form.submit() bypasses the submit event and constraint validation. To prevent this, we can inject a
        // native submit button into the form, "click" it, then remove it to simulate a standard form submission.
        this.doAction('submit', submitter);
    }
    /**
     * Synchronously sets the form control's validity. Call this when you know the future validity but need to update
     * the host element immediately, i.e. before Lit updates the component in the next update.
     */
    setValidity(isValid) {
        const host = this.host;
        const hasInteracted = Boolean(userInteractedControls.has(host));
        const required = Boolean(host.required);
        //
        // We're mapping the following "states" to data attributes. In the future, we can use ElementInternals.states to
        // create a similar mapping, but instead of [data-invalid] it will look like :--invalid.
        //
        // See this RFC for more details: https://github.com/shoelace-style/shoelace/issues/1011
        //
        host.toggleAttribute('data-required', required);
        host.toggleAttribute('data-optional', !required);
        host.toggleAttribute('data-invalid', !isValid);
        host.toggleAttribute('data-valid', isValid);
        host.toggleAttribute('data-user-invalid', !isValid && hasInteracted);
        host.toggleAttribute('data-user-valid', isValid && hasInteracted);
    }
    userInteracted() {
        const hasInteracted = Boolean(userInteractedControls.has(this.host));
        return hasInteracted;
    }
    /**
     * Updates the form control's validity based on the current value of `host.validity.valid`. Call this when anything
     * that affects constraint validation changes so the component receives the correct validity states.
     */
    updateValidity() {
        const host = this.host;
        this.setValidity(host.validity.valid);
    }
    /**
     * Dispatches a non-bubbling, cancelable custom event of type `it-invalid`.
     * If the `it-invalid` event will be cancelled then the original `invalid`
     * event (which may have been passed as argument) will also be cancelled.
     * If no original `invalid` event has been passed then the `it-invalid`
     * event will be cancelled before being dispatched.
     */
    emitInvalidEvent(originalInvalidEvent) {
        const itInvalidEvent = new CustomEvent('it-invalid', {
            bubbles: false,
            composed: false,
            cancelable: true,
            detail: {},
        });
        if (!originalInvalidEvent) {
            itInvalidEvent.preventDefault();
        }
        if (!this.host.dispatchEvent(itInvalidEvent)) {
            originalInvalidEvent?.preventDefault();
        }
    }
}

const translation = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    validityRequired: 'Questo campo è obbligatorio.',
    validityGroupRequired: "Scegli almeno un'opzione",
    validityPattern: 'Il valore non corrisponde al formato richiesto.',
    validityMinlength: 'Il valore deve essere lungo almeno {minlength} caratteri.',
    validityMaxlength: 'Il valore deve essere lungo al massimo {maxlength} caratteri.',
};

registerTranslation(translation);
class FormControl extends BaseLocalizedComponent {
    constructor() {
        super(...arguments);
        this.formControlController = new FormControlController(this, {
            assumeInteractionOn: ['it-input', 'it-blur', 'it-change'],
        });
        // TODO: verificare se serve davvero con il fatto che usiamo form-controller
        // static formAssociated = true;
        // @property()
        // internals = this.attachInternals();
        this._touched = false;
        /** The name of the input, submitted as a name/value pair with form data. */
        this.name = '';
        /** The current value of the input, submitted as a name/value pair with form data. */
        this.value = '';
        /** If the input is disabled. */
        this.disabled = false;
        /**
         * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
         * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
         * the same document or shadow root for this to work.
         */
        this.form = '';
        /** If you implement your custom validation and you won't to trigger default validation */
        this.customValidation = false;
        /** If your input is invalid from your custom validition, set this attribute with message validation */
        this.validationText = '';
        /**
         * Specifies the granularity that the value must adhere to, or the special value `any` which means no stepping is
         * implied, allowing any numeric value. Only applies to date and number input types.
         */
        this.step = 'any';
        /** The input's minimum length. */
        this.minlength = -1;
        /** The input's maximum length. */
        this.maxlength = -1;
        /** If the input is required. */
        this.required = false;
        /* For grouped input, like checkbox-group */
        this.isInGroup = false;
        this.validationMessage = '';
    }
    /** Gets the validity state object */
    get validity() {
        return this.inputElement?.validity;
    }
    /** Gets the associated form, if one exists. */
    getForm() {
        return this.formControlController.getForm();
    }
    // Form validation methods
    checkValidity() {
        const inputValid = this.inputElement?.checkValidity() ?? true; // this.inputElement.checkValidity() è la validazione del browser
        return inputValid;
    }
    /** Checks for validity and shows the browser's validation message if the control is invalid. */
    reportValidity() {
        // const ret = this.inputElement.checkValidity();
        const ret = this.checkValidity(); // chiama la checkValidity, che se è stata overridata chiama quella
        this.handleValidationMessages();
        return ret; // this.inputElement.reportValidity();
    }
    /** Sets a custom validation message. Pass an empty string to restore validity. */
    setCustomValidity(message) {
        this.inputElement.setCustomValidity(message);
        this.validationMessage = this.inputElement.validationMessage;
        this.formControlController.updateValidity();
    }
    // Handlers
    _handleReady() {
        requestAnimationFrame(() => {
            this.dispatchEvent(new CustomEvent('it-input-ready', { bubbles: true, detail: { el: this.inputElement } }));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleInput(e) {
        this.handleValidationMessages();
        this.dispatchEvent(new CustomEvent('it-input', {
            detail: { value: this.inputElement.value, el: this.inputElement },
            bubbles: true,
            composed: true,
        }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleBlur(e) {
        this._touched = true;
        this.handleValidationMessages();
        this.dispatchEvent(new FocusEvent('it-blur', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleFocus(e) {
        this.dispatchEvent(new FocusEvent('it-focus', { bubbles: true, composed: true }));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _handleClick(e) {
        this.dispatchEvent(new MouseEvent('it-click', { bubbles: true, composed: true }));
    }
    /*
    Override default browser validation messages
     */
    handleValidationMessages() {
        if (!this.customValidation) {
            const _v = this.inputElement.validity;
            const isRequiredHandledByGroup = this.isInGroup === true;
            if (_v.valueMissing && !isRequiredHandledByGroup) {
                this.setCustomValidity(this.$t('validityRequired'));
            }
            else if (_v.patternMismatch) {
                this.setCustomValidity(this.$t('validityPattern'));
            }
            else if (_v.tooShort) {
                this.setCustomValidity(this.$t('validityMinlength').replace('{minlength}', this.minlength.toString()));
            }
            else if (_v.tooLong) {
                this.setCustomValidity(this.$t('validityMaxlength').replace('{maxlength}', this.maxlength.toString()));
            }
            else {
                /* nothing. Usa il messaggio di errore della validazione
                di default del browser per altri errori di validità come:
                - typeMismatch
                - rangeUnderflow
                - rangeOverflow
                - stepMismatch
                - badInput */
                const otherConstraintErrors = _v.typeMismatch || _v.rangeUnderflow || _v.rangeOverflow || _v.stepMismatch || _v.badInput;
                if (!otherConstraintErrors) {
                    this.setCustomValidity('');
                }
            }
        }
        this.validationMessage = this.inputElement.validationMessage;
    }
    _handleInvalid(event) {
        this.formControlController.setValidity(false);
        this.formControlController.emitInvalidEvent(event);
    }
    _handleChange(e) {
        const target = e.target;
        let value;
        if (target instanceof HTMLInputElement) {
            switch (target.type) {
                case 'checkbox':
                case 'radio':
                    value = target.checked;
                    break;
                case 'file':
                    value = target.files; // FileList
                    break;
                default:
                    value = target.value;
            }
        }
        else if (target instanceof HTMLSelectElement) {
            if (target.multiple) {
                value = Array.from(target.selectedOptions).map((o) => o.value);
            }
            else {
                value = target.value;
            }
        }
        else {
            // textarea o altri input con value
            value = target.value;
        }
        this.dispatchEvent(new CustomEvent('it-change', {
            detail: { value, el: target },
            bubbles: true,
            composed: true,
        }));
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        if (this.customValidation) {
            this.setCustomValidity(this.validationText ?? '');
        }
        else if (this.formControlController.userInteracted()) {
            this.formControlController.updateValidity();
        }
    }
}
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "_touched", void 0);
__decorate([
    query('.it-form__control'),
    __metadata("design:type", HTMLInputElement)
], FormControl.prototype, "inputElement", void 0);
__decorate([
    property({ type: String, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "name", void 0);
__decorate([
    property({ reflect: true }),
    __metadata("design:type", Object)
], FormControl.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: String }),
    __metadata("design:type", Object)
], FormControl.prototype, "form", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'custom-validation' }),
    __metadata("design:type", Object)
], FormControl.prototype, "customValidation", void 0);
__decorate([
    property({ attribute: 'validity-message', reflect: true }),
    __metadata("design:type", String)
], FormControl.prototype, "validationText", void 0);
__decorate([
    property(),
    __metadata("design:type", String)
], FormControl.prototype, "pattern", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "min", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "max", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], FormControl.prototype, "step", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "minlength", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Object)
], FormControl.prototype, "maxlength", void 0);
__decorate([
    property({ type: Boolean, reflect: true }) // from FormControl
    ,
    __metadata("design:type", Object)
], FormControl.prototype, "required", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], FormControl.prototype, "isInGroup", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], FormControl.prototype, "validationMessage", void 0);

if (typeof window !== 'undefined') {
    window._itAnalytics = window._itAnalytics || {};
    window._itAnalytics = {
        ...window._itAnalytics,
        version: '1.0.0-beta.3',
    };
}

/**
 * Custom positioning reference element.
 * @see https://floating-ui.com/docs/virtual-elements
 */

const min = Math.min;
const max = Math.max;
const round = Math.round;
const floor = Math.floor;
const createCoords = v => ({
  x: v,
  y: v
});
const oppositeSideMap = {
  left: 'right',
  right: 'left',
  bottom: 'top',
  top: 'bottom'
};
function clamp(start, value, end) {
  return max(start, min(value, end));
}
function evaluate(value, param) {
  return typeof value === 'function' ? value(param) : value;
}
function getSide(placement) {
  return placement.split('-')[0];
}
function getAlignment(placement) {
  return placement.split('-')[1];
}
function getOppositeAxis(axis) {
  return axis === 'x' ? 'y' : 'x';
}
function getAxisLength(axis) {
  return axis === 'y' ? 'height' : 'width';
}
function getSideAxis(placement) {
  const firstChar = placement[0];
  return firstChar === 't' || firstChar === 'b' ? 'y' : 'x';
}
function getAlignmentAxis(placement) {
  return getOppositeAxis(getSideAxis(placement));
}
function getAlignmentSides(placement, rects, rtl) {
  if (rtl === void 0) {
    rtl = false;
  }
  const alignment = getAlignment(placement);
  const alignmentAxis = getAlignmentAxis(placement);
  const length = getAxisLength(alignmentAxis);
  let mainAlignmentSide = alignmentAxis === 'x' ? alignment === (rtl ? 'end' : 'start') ? 'right' : 'left' : alignment === 'start' ? 'bottom' : 'top';
  if (rects.reference[length] > rects.floating[length]) {
    mainAlignmentSide = getOppositePlacement(mainAlignmentSide);
  }
  return [mainAlignmentSide, getOppositePlacement(mainAlignmentSide)];
}
function getExpandedPlacements(placement) {
  const oppositePlacement = getOppositePlacement(placement);
  return [getOppositeAlignmentPlacement(placement), oppositePlacement, getOppositeAlignmentPlacement(oppositePlacement)];
}
function getOppositeAlignmentPlacement(placement) {
  return placement.includes('start') ? placement.replace('start', 'end') : placement.replace('end', 'start');
}
const lrPlacement = ['left', 'right'];
const rlPlacement = ['right', 'left'];
const tbPlacement = ['top', 'bottom'];
const btPlacement = ['bottom', 'top'];
function getSideList(side, isStart, rtl) {
  switch (side) {
    case 'top':
    case 'bottom':
      if (rtl) return isStart ? rlPlacement : lrPlacement;
      return isStart ? lrPlacement : rlPlacement;
    case 'left':
    case 'right':
      return isStart ? tbPlacement : btPlacement;
    default:
      return [];
  }
}
function getOppositeAxisPlacements(placement, flipAlignment, direction, rtl) {
  const alignment = getAlignment(placement);
  let list = getSideList(getSide(placement), direction === 'start', rtl);
  if (alignment) {
    list = list.map(side => side + "-" + alignment);
    if (flipAlignment) {
      list = list.concat(list.map(getOppositeAlignmentPlacement));
    }
  }
  return list;
}
function getOppositePlacement(placement) {
  const side = getSide(placement);
  return oppositeSideMap[side] + placement.slice(side.length);
}
function expandPaddingObject(padding) {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    ...padding
  };
}
function getPaddingObject(padding) {
  return typeof padding !== 'number' ? expandPaddingObject(padding) : {
    top: padding,
    right: padding,
    bottom: padding,
    left: padding
  };
}
function rectToClientRect(rect) {
  const {
    x,
    y,
    width,
    height
  } = rect;
  return {
    width,
    height,
    top: y,
    left: x,
    right: x + width,
    bottom: y + height,
    x,
    y
  };
}

function computeCoordsFromPlacement(_ref, placement, rtl) {
  let {
    reference,
    floating
  } = _ref;
  const sideAxis = getSideAxis(placement);
  const alignmentAxis = getAlignmentAxis(placement);
  const alignLength = getAxisLength(alignmentAxis);
  const side = getSide(placement);
  const isVertical = sideAxis === 'y';
  const commonX = reference.x + reference.width / 2 - floating.width / 2;
  const commonY = reference.y + reference.height / 2 - floating.height / 2;
  const commonAlign = reference[alignLength] / 2 - floating[alignLength] / 2;
  let coords;
  switch (side) {
    case 'top':
      coords = {
        x: commonX,
        y: reference.y - floating.height
      };
      break;
    case 'bottom':
      coords = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;
    case 'right':
      coords = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;
    case 'left':
      coords = {
        x: reference.x - floating.width,
        y: commonY
      };
      break;
    default:
      coords = {
        x: reference.x,
        y: reference.y
      };
  }
  switch (getAlignment(placement)) {
    case 'start':
      coords[alignmentAxis] -= commonAlign * (rtl && isVertical ? -1 : 1);
      break;
    case 'end':
      coords[alignmentAxis] += commonAlign * (rtl && isVertical ? -1 : 1);
      break;
  }
  return coords;
}

/**
 * Resolves with an object of overflow side offsets that determine how much the
 * element is overflowing a given clipping boundary on each side.
 * - positive = overflowing the boundary by that number of pixels
 * - negative = how many pixels left before it will overflow
 * - 0 = lies flush with the boundary
 * @see https://floating-ui.com/docs/detectOverflow
 */
async function detectOverflow(state, options) {
  var _await$platform$isEle;
  if (options === void 0) {
    options = {};
  }
  const {
    x,
    y,
    platform,
    rects,
    elements,
    strategy
  } = state;
  const {
    boundary = 'clippingAncestors',
    rootBoundary = 'viewport',
    elementContext = 'floating',
    altBoundary = false,
    padding = 0
  } = evaluate(options, state);
  const paddingObject = getPaddingObject(padding);
  const altContext = elementContext === 'floating' ? 'reference' : 'floating';
  const element = elements[altBoundary ? altContext : elementContext];
  const clippingClientRect = rectToClientRect(await platform.getClippingRect({
    element: ((_await$platform$isEle = await (platform.isElement == null ? void 0 : platform.isElement(element))) != null ? _await$platform$isEle : true) ? element : element.contextElement || (await (platform.getDocumentElement == null ? void 0 : platform.getDocumentElement(elements.floating))),
    boundary,
    rootBoundary,
    strategy
  }));
  const rect = elementContext === 'floating' ? {
    x,
    y,
    width: rects.floating.width,
    height: rects.floating.height
  } : rects.reference;
  const offsetParent = await (platform.getOffsetParent == null ? void 0 : platform.getOffsetParent(elements.floating));
  const offsetScale = (await (platform.isElement == null ? void 0 : platform.isElement(offsetParent))) ? (await (platform.getScale == null ? void 0 : platform.getScale(offsetParent))) || {
    x: 1,
    y: 1
  } : {
    x: 1,
    y: 1
  };
  const elementClientRect = rectToClientRect(platform.convertOffsetParentRelativeRectToViewportRelativeRect ? await platform.convertOffsetParentRelativeRectToViewportRelativeRect({
    elements,
    rect,
    offsetParent,
    strategy
  }) : rect);
  return {
    top: (clippingClientRect.top - elementClientRect.top + paddingObject.top) / offsetScale.y,
    bottom: (elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom) / offsetScale.y,
    left: (clippingClientRect.left - elementClientRect.left + paddingObject.left) / offsetScale.x,
    right: (elementClientRect.right - clippingClientRect.right + paddingObject.right) / offsetScale.x
  };
}

// Maximum number of resets that can occur before bailing to avoid infinite reset loops.
const MAX_RESET_COUNT = 50;

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a given reference element.
 *
 * This export does not have any `platform` interface logic. You will need to
 * write one for the platform you are using Floating UI with.
 */
const computePosition$1 = async (reference, floating, config) => {
  const {
    placement = 'bottom',
    strategy = 'absolute',
    middleware = [],
    platform
  } = config;
  const platformWithDetectOverflow = platform.detectOverflow ? platform : {
    ...platform,
    detectOverflow
  };
  const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(floating));
  let rects = await platform.getElementRects({
    reference,
    floating,
    strategy
  });
  let {
    x,
    y
  } = computeCoordsFromPlacement(rects, placement, rtl);
  let statefulPlacement = placement;
  let resetCount = 0;
  const middlewareData = {};
  for (let i = 0; i < middleware.length; i++) {
    const currentMiddleware = middleware[i];
    if (!currentMiddleware) {
      continue;
    }
    const {
      name,
      fn
    } = currentMiddleware;
    const {
      x: nextX,
      y: nextY,
      data,
      reset
    } = await fn({
      x,
      y,
      initialPlacement: placement,
      placement: statefulPlacement,
      strategy,
      middlewareData,
      rects,
      platform: platformWithDetectOverflow,
      elements: {
        reference,
        floating
      }
    });
    x = nextX != null ? nextX : x;
    y = nextY != null ? nextY : y;
    middlewareData[name] = {
      ...middlewareData[name],
      ...data
    };
    if (reset && resetCount < MAX_RESET_COUNT) {
      resetCount++;
      if (typeof reset === 'object') {
        if (reset.placement) {
          statefulPlacement = reset.placement;
        }
        if (reset.rects) {
          rects = reset.rects === true ? await platform.getElementRects({
            reference,
            floating,
            strategy
          }) : reset.rects;
        }
        ({
          x,
          y
        } = computeCoordsFromPlacement(rects, statefulPlacement, rtl));
      }
      i = -1;
    }
  }
  return {
    x,
    y,
    placement: statefulPlacement,
    strategy,
    middlewareData
  };
};

/**
 * Provides data to position an inner element of the floating element so that it
 * appears centered to the reference element.
 * @see https://floating-ui.com/docs/arrow
 */
const arrow$1 = options => ({
  name: 'arrow',
  options,
  async fn(state) {
    const {
      x,
      y,
      placement,
      rects,
      platform,
      elements,
      middlewareData
    } = state;
    // Since `element` is required, we don't Partial<> the type.
    const {
      element,
      padding = 0
    } = evaluate(options, state) || {};
    if (element == null) {
      return {};
    }
    const paddingObject = getPaddingObject(padding);
    const coords = {
      x,
      y
    };
    const axis = getAlignmentAxis(placement);
    const length = getAxisLength(axis);
    const arrowDimensions = await platform.getDimensions(element);
    const isYAxis = axis === 'y';
    const minProp = isYAxis ? 'top' : 'left';
    const maxProp = isYAxis ? 'bottom' : 'right';
    const clientProp = isYAxis ? 'clientHeight' : 'clientWidth';
    const endDiff = rects.reference[length] + rects.reference[axis] - coords[axis] - rects.floating[length];
    const startDiff = coords[axis] - rects.reference[axis];
    const arrowOffsetParent = await (platform.getOffsetParent == null ? void 0 : platform.getOffsetParent(element));
    let clientSize = arrowOffsetParent ? arrowOffsetParent[clientProp] : 0;

    // DOM platform can return `window` as the `offsetParent`.
    if (!clientSize || !(await (platform.isElement == null ? void 0 : platform.isElement(arrowOffsetParent)))) {
      clientSize = elements.floating[clientProp] || rects.floating[length];
    }
    const centerToReference = endDiff / 2 - startDiff / 2;

    // If the padding is large enough that it causes the arrow to no longer be
    // centered, modify the padding so that it is centered.
    const largestPossiblePadding = clientSize / 2 - arrowDimensions[length] / 2 - 1;
    const minPadding = min(paddingObject[minProp], largestPossiblePadding);
    const maxPadding = min(paddingObject[maxProp], largestPossiblePadding);

    // Make sure the arrow doesn't overflow the floating element if the center
    // point is outside the floating element's bounds.
    const min$1 = minPadding;
    const max = clientSize - arrowDimensions[length] - maxPadding;
    const center = clientSize / 2 - arrowDimensions[length] / 2 + centerToReference;
    const offset = clamp(min$1, center, max);

    // If the reference is small enough that the arrow's padding causes it to
    // to point to nothing for an aligned placement, adjust the offset of the
    // floating element itself. To ensure `shift()` continues to take action,
    // a single reset is performed when this is true.
    const shouldAddOffset = !middlewareData.arrow && getAlignment(placement) != null && center !== offset && rects.reference[length] / 2 - (center < min$1 ? minPadding : maxPadding) - arrowDimensions[length] / 2 < 0;
    const alignmentOffset = shouldAddOffset ? center < min$1 ? center - min$1 : center - max : 0;
    return {
      [axis]: coords[axis] + alignmentOffset,
      data: {
        [axis]: offset,
        centerOffset: center - offset - alignmentOffset,
        ...(shouldAddOffset && {
          alignmentOffset
        })
      },
      reset: shouldAddOffset
    };
  }
});

/**
 * Optimizes the visibility of the floating element by flipping the `placement`
 * in order to keep it in view when the preferred placement(s) will overflow the
 * clipping boundary. Alternative to `autoPlacement`.
 * @see https://floating-ui.com/docs/flip
 */
const flip$1 = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'flip',
    options,
    async fn(state) {
      var _middlewareData$arrow, _middlewareData$flip;
      const {
        placement,
        middlewareData,
        rects,
        initialPlacement,
        platform,
        elements
      } = state;
      const {
        mainAxis: checkMainAxis = true,
        crossAxis: checkCrossAxis = true,
        fallbackPlacements: specifiedFallbackPlacements,
        fallbackStrategy = 'bestFit',
        fallbackAxisSideDirection = 'none',
        flipAlignment = true,
        ...detectOverflowOptions
      } = evaluate(options, state);

      // If a reset by the arrow was caused due to an alignment offset being
      // added, we should skip any logic now since `flip()` has already done its
      // work.
      // https://github.com/floating-ui/floating-ui/issues/2549#issuecomment-1719601643
      if ((_middlewareData$arrow = middlewareData.arrow) != null && _middlewareData$arrow.alignmentOffset) {
        return {};
      }
      const side = getSide(placement);
      const initialSideAxis = getSideAxis(initialPlacement);
      const isBasePlacement = getSide(initialPlacement) === initialPlacement;
      const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating));
      const fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipAlignment ? [getOppositePlacement(initialPlacement)] : getExpandedPlacements(initialPlacement));
      const hasFallbackAxisSideDirection = fallbackAxisSideDirection !== 'none';
      if (!specifiedFallbackPlacements && hasFallbackAxisSideDirection) {
        fallbackPlacements.push(...getOppositeAxisPlacements(initialPlacement, flipAlignment, fallbackAxisSideDirection, rtl));
      }
      const placements = [initialPlacement, ...fallbackPlacements];
      const overflow = await platform.detectOverflow(state, detectOverflowOptions);
      const overflows = [];
      let overflowsData = ((_middlewareData$flip = middlewareData.flip) == null ? void 0 : _middlewareData$flip.overflows) || [];
      if (checkMainAxis) {
        overflows.push(overflow[side]);
      }
      if (checkCrossAxis) {
        const sides = getAlignmentSides(placement, rects, rtl);
        overflows.push(overflow[sides[0]], overflow[sides[1]]);
      }
      overflowsData = [...overflowsData, {
        placement,
        overflows
      }];

      // One or more sides is overflowing.
      if (!overflows.every(side => side <= 0)) {
        var _middlewareData$flip2, _overflowsData$filter;
        const nextIndex = (((_middlewareData$flip2 = middlewareData.flip) == null ? void 0 : _middlewareData$flip2.index) || 0) + 1;
        const nextPlacement = placements[nextIndex];
        if (nextPlacement) {
          const ignoreCrossAxisOverflow = checkCrossAxis === 'alignment' ? initialSideAxis !== getSideAxis(nextPlacement) : false;
          if (!ignoreCrossAxisOverflow ||
          // We leave the current main axis only if every placement on that axis
          // overflows the main axis.
          overflowsData.every(d => getSideAxis(d.placement) === initialSideAxis ? d.overflows[0] > 0 : true)) {
            // Try next placement and re-run the lifecycle.
            return {
              data: {
                index: nextIndex,
                overflows: overflowsData
              },
              reset: {
                placement: nextPlacement
              }
            };
          }
        }

        // First, find the candidates that fit on the mainAxis side of overflow,
        // then find the placement that fits the best on the main crossAxis side.
        let resetPlacement = (_overflowsData$filter = overflowsData.filter(d => d.overflows[0] <= 0).sort((a, b) => a.overflows[1] - b.overflows[1])[0]) == null ? void 0 : _overflowsData$filter.placement;

        // Otherwise fallback.
        if (!resetPlacement) {
          switch (fallbackStrategy) {
            case 'bestFit':
              {
                var _overflowsData$filter2;
                const placement = (_overflowsData$filter2 = overflowsData.filter(d => {
                  if (hasFallbackAxisSideDirection) {
                    const currentSideAxis = getSideAxis(d.placement);
                    return currentSideAxis === initialSideAxis ||
                    // Create a bias to the `y` side axis due to horizontal
                    // reading directions favoring greater width.
                    currentSideAxis === 'y';
                  }
                  return true;
                }).map(d => [d.placement, d.overflows.filter(overflow => overflow > 0).reduce((acc, overflow) => acc + overflow, 0)]).sort((a, b) => a[1] - b[1])[0]) == null ? void 0 : _overflowsData$filter2[0];
                if (placement) {
                  resetPlacement = placement;
                }
                break;
              }
            case 'initialPlacement':
              resetPlacement = initialPlacement;
              break;
          }
        }
        if (placement !== resetPlacement) {
          return {
            reset: {
              placement: resetPlacement
            }
          };
        }
      }
      return {};
    }
  };
};

const originSides = /*#__PURE__*/new Set(['left', 'top']);

// For type backwards-compatibility, the `OffsetOptions` type was also
// Derivable.

async function convertValueToCoords(state, options) {
  const {
    placement,
    platform,
    elements
  } = state;
  const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(elements.floating));
  const side = getSide(placement);
  const alignment = getAlignment(placement);
  const isVertical = getSideAxis(placement) === 'y';
  const mainAxisMulti = originSides.has(side) ? -1 : 1;
  const crossAxisMulti = rtl && isVertical ? -1 : 1;
  const rawValue = evaluate(options, state);

  // eslint-disable-next-line prefer-const
  let {
    mainAxis,
    crossAxis,
    alignmentAxis
  } = typeof rawValue === 'number' ? {
    mainAxis: rawValue,
    crossAxis: 0,
    alignmentAxis: null
  } : {
    mainAxis: rawValue.mainAxis || 0,
    crossAxis: rawValue.crossAxis || 0,
    alignmentAxis: rawValue.alignmentAxis
  };
  if (alignment && typeof alignmentAxis === 'number') {
    crossAxis = alignment === 'end' ? alignmentAxis * -1 : alignmentAxis;
  }
  return isVertical ? {
    x: crossAxis * crossAxisMulti,
    y: mainAxis * mainAxisMulti
  } : {
    x: mainAxis * mainAxisMulti,
    y: crossAxis * crossAxisMulti
  };
}

/**
 * Modifies the placement by translating the floating element along the
 * specified axes.
 * A number (shorthand for `mainAxis` or distance), or an axes configuration
 * object may be passed.
 * @see https://floating-ui.com/docs/offset
 */
const offset$1 = function (options) {
  if (options === void 0) {
    options = 0;
  }
  return {
    name: 'offset',
    options,
    async fn(state) {
      var _middlewareData$offse, _middlewareData$arrow;
      const {
        x,
        y,
        placement,
        middlewareData
      } = state;
      const diffCoords = await convertValueToCoords(state, options);

      // If the placement is the same and the arrow caused an alignment offset
      // then we don't need to change the positioning coordinates.
      if (placement === ((_middlewareData$offse = middlewareData.offset) == null ? void 0 : _middlewareData$offse.placement) && (_middlewareData$arrow = middlewareData.arrow) != null && _middlewareData$arrow.alignmentOffset) {
        return {};
      }
      return {
        x: x + diffCoords.x,
        y: y + diffCoords.y,
        data: {
          ...diffCoords,
          placement
        }
      };
    }
  };
};

/**
 * Optimizes the visibility of the floating element by shifting it in order to
 * keep it in view when it will overflow the clipping boundary.
 * @see https://floating-ui.com/docs/shift
 */
const shift$1 = function (options) {
  if (options === void 0) {
    options = {};
  }
  return {
    name: 'shift',
    options,
    async fn(state) {
      const {
        x,
        y,
        placement,
        platform
      } = state;
      const {
        mainAxis: checkMainAxis = true,
        crossAxis: checkCrossAxis = false,
        limiter = {
          fn: _ref => {
            let {
              x,
              y
            } = _ref;
            return {
              x,
              y
            };
          }
        },
        ...detectOverflowOptions
      } = evaluate(options, state);
      const coords = {
        x,
        y
      };
      const overflow = await platform.detectOverflow(state, detectOverflowOptions);
      const crossAxis = getSideAxis(getSide(placement));
      const mainAxis = getOppositeAxis(crossAxis);
      let mainAxisCoord = coords[mainAxis];
      let crossAxisCoord = coords[crossAxis];
      if (checkMainAxis) {
        const minSide = mainAxis === 'y' ? 'top' : 'left';
        const maxSide = mainAxis === 'y' ? 'bottom' : 'right';
        const min = mainAxisCoord + overflow[minSide];
        const max = mainAxisCoord - overflow[maxSide];
        mainAxisCoord = clamp(min, mainAxisCoord, max);
      }
      if (checkCrossAxis) {
        const minSide = crossAxis === 'y' ? 'top' : 'left';
        const maxSide = crossAxis === 'y' ? 'bottom' : 'right';
        const min = crossAxisCoord + overflow[minSide];
        const max = crossAxisCoord - overflow[maxSide];
        crossAxisCoord = clamp(min, crossAxisCoord, max);
      }
      const limitedCoords = limiter.fn({
        ...state,
        [mainAxis]: mainAxisCoord,
        [crossAxis]: crossAxisCoord
      });
      return {
        ...limitedCoords,
        data: {
          x: limitedCoords.x - x,
          y: limitedCoords.y - y,
          enabled: {
            [mainAxis]: checkMainAxis,
            [crossAxis]: checkCrossAxis
          }
        }
      };
    }
  };
};

function hasWindow() {
  return typeof window !== 'undefined';
}
function getNodeName(node) {
  if (isNode(node)) {
    return (node.nodeName || '').toLowerCase();
  }
  // Mocked nodes in testing environments may not be instances of Node. By
  // returning `#document` an infinite loop won't occur.
  // https://github.com/floating-ui/floating-ui/issues/2317
  return '#document';
}
function getWindow(node) {
  var _node$ownerDocument;
  return (node == null || (_node$ownerDocument = node.ownerDocument) == null ? void 0 : _node$ownerDocument.defaultView) || window;
}
function getDocumentElement(node) {
  var _ref;
  return (_ref = (isNode(node) ? node.ownerDocument : node.document) || window.document) == null ? void 0 : _ref.documentElement;
}
function isNode(value) {
  if (!hasWindow()) {
    return false;
  }
  return value instanceof Node || value instanceof getWindow(value).Node;
}
function isElement(value) {
  if (!hasWindow()) {
    return false;
  }
  return value instanceof Element || value instanceof getWindow(value).Element;
}
function isHTMLElement(value) {
  if (!hasWindow()) {
    return false;
  }
  return value instanceof HTMLElement || value instanceof getWindow(value).HTMLElement;
}
function isShadowRoot(value) {
  if (!hasWindow() || typeof ShadowRoot === 'undefined') {
    return false;
  }
  return value instanceof ShadowRoot || value instanceof getWindow(value).ShadowRoot;
}
function isOverflowElement(element) {
  const {
    overflow,
    overflowX,
    overflowY,
    display
  } = getComputedStyle$1(element);
  return /auto|scroll|overlay|hidden|clip/.test(overflow + overflowY + overflowX) && display !== 'inline' && display !== 'contents';
}
function isTableElement(element) {
  return /^(table|td|th)$/.test(getNodeName(element));
}
function isTopLayer(element) {
  try {
    if (element.matches(':popover-open')) {
      return true;
    }
  } catch (_e) {
    // no-op
  }
  try {
    return element.matches(':modal');
  } catch (_e) {
    return false;
  }
}
const willChangeRe = /transform|translate|scale|rotate|perspective|filter/;
const containRe = /paint|layout|strict|content/;
const isNotNone = value => !!value && value !== 'none';
let isWebKitValue;
function isContainingBlock(elementOrCss) {
  const css = isElement(elementOrCss) ? getComputedStyle$1(elementOrCss) : elementOrCss;

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  // https://drafts.csswg.org/css-transforms-2/#individual-transforms
  return isNotNone(css.transform) || isNotNone(css.translate) || isNotNone(css.scale) || isNotNone(css.rotate) || isNotNone(css.perspective) || !isWebKit() && (isNotNone(css.backdropFilter) || isNotNone(css.filter)) || willChangeRe.test(css.willChange || '') || containRe.test(css.contain || '');
}
function getContainingBlock(element) {
  let currentNode = getParentNode(element);
  while (isHTMLElement(currentNode) && !isLastTraversableNode(currentNode)) {
    if (isContainingBlock(currentNode)) {
      return currentNode;
    } else if (isTopLayer(currentNode)) {
      return null;
    }
    currentNode = getParentNode(currentNode);
  }
  return null;
}
function isWebKit() {
  if (isWebKitValue == null) {
    isWebKitValue = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('-webkit-backdrop-filter', 'none');
  }
  return isWebKitValue;
}
function isLastTraversableNode(node) {
  return /^(html|body|#document)$/.test(getNodeName(node));
}
function getComputedStyle$1(element) {
  return getWindow(element).getComputedStyle(element);
}
function getNodeScroll(element) {
  if (isElement(element)) {
    return {
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop
    };
  }
  return {
    scrollLeft: element.scrollX,
    scrollTop: element.scrollY
  };
}
function getParentNode(node) {
  if (getNodeName(node) === 'html') {
    return node;
  }
  const result =
  // Step into the shadow DOM of the parent of a slotted node.
  node.assignedSlot ||
  // DOM Element detected.
  node.parentNode ||
  // ShadowRoot detected.
  isShadowRoot(node) && node.host ||
  // Fallback.
  getDocumentElement(node);
  return isShadowRoot(result) ? result.host : result;
}
function getNearestOverflowAncestor(node) {
  const parentNode = getParentNode(node);
  if (isLastTraversableNode(parentNode)) {
    return node.ownerDocument ? node.ownerDocument.body : node.body;
  }
  if (isHTMLElement(parentNode) && isOverflowElement(parentNode)) {
    return parentNode;
  }
  return getNearestOverflowAncestor(parentNode);
}
function getOverflowAncestors(node, list, traverseIframes) {
  var _node$ownerDocument2;
  if (list === void 0) {
    list = [];
  }
  if (traverseIframes === void 0) {
    traverseIframes = true;
  }
  const scrollableAncestor = getNearestOverflowAncestor(node);
  const isBody = scrollableAncestor === ((_node$ownerDocument2 = node.ownerDocument) == null ? void 0 : _node$ownerDocument2.body);
  const win = getWindow(scrollableAncestor);
  if (isBody) {
    const frameElement = getFrameElement(win);
    return list.concat(win, win.visualViewport || [], isOverflowElement(scrollableAncestor) ? scrollableAncestor : [], frameElement && traverseIframes ? getOverflowAncestors(frameElement) : []);
  } else {
    return list.concat(scrollableAncestor, getOverflowAncestors(scrollableAncestor, [], traverseIframes));
  }
}
function getFrameElement(win) {
  return win.parent && Object.getPrototypeOf(win.parent) ? win.frameElement : null;
}

function getCssDimensions(element) {
  const css = getComputedStyle$1(element);
  // In testing environments, the `width` and `height` properties are empty
  // strings for SVG elements, returning NaN. Fallback to `0` in this case.
  let width = parseFloat(css.width) || 0;
  let height = parseFloat(css.height) || 0;
  const hasOffset = isHTMLElement(element);
  const offsetWidth = hasOffset ? element.offsetWidth : width;
  const offsetHeight = hasOffset ? element.offsetHeight : height;
  const shouldFallback = round(width) !== offsetWidth || round(height) !== offsetHeight;
  if (shouldFallback) {
    width = offsetWidth;
    height = offsetHeight;
  }
  return {
    width,
    height,
    $: shouldFallback
  };
}

function unwrapElement(element) {
  return !isElement(element) ? element.contextElement : element;
}

function getScale(element) {
  const domElement = unwrapElement(element);
  if (!isHTMLElement(domElement)) {
    return createCoords(1);
  }
  const rect = domElement.getBoundingClientRect();
  const {
    width,
    height,
    $
  } = getCssDimensions(domElement);
  let x = ($ ? round(rect.width) : rect.width) / width;
  let y = ($ ? round(rect.height) : rect.height) / height;

  // 0, NaN, or Infinity should always fallback to 1.

  if (!x || !Number.isFinite(x)) {
    x = 1;
  }
  if (!y || !Number.isFinite(y)) {
    y = 1;
  }
  return {
    x,
    y
  };
}

const noOffsets = /*#__PURE__*/createCoords(0);
function getVisualOffsets(element) {
  const win = getWindow(element);
  if (!isWebKit() || !win.visualViewport) {
    return noOffsets;
  }
  return {
    x: win.visualViewport.offsetLeft,
    y: win.visualViewport.offsetTop
  };
}
function shouldAddVisualOffsets(element, isFixed, floatingOffsetParent) {
  if (isFixed === void 0) {
    isFixed = false;
  }
  if (!floatingOffsetParent || isFixed && floatingOffsetParent !== getWindow(element)) {
    return false;
  }
  return isFixed;
}

function getBoundingClientRect(element, includeScale, isFixedStrategy, offsetParent) {
  if (includeScale === void 0) {
    includeScale = false;
  }
  if (isFixedStrategy === void 0) {
    isFixedStrategy = false;
  }
  const clientRect = element.getBoundingClientRect();
  const domElement = unwrapElement(element);
  let scale = createCoords(1);
  if (includeScale) {
    if (offsetParent) {
      if (isElement(offsetParent)) {
        scale = getScale(offsetParent);
      }
    } else {
      scale = getScale(element);
    }
  }
  const visualOffsets = shouldAddVisualOffsets(domElement, isFixedStrategy, offsetParent) ? getVisualOffsets(domElement) : createCoords(0);
  let x = (clientRect.left + visualOffsets.x) / scale.x;
  let y = (clientRect.top + visualOffsets.y) / scale.y;
  let width = clientRect.width / scale.x;
  let height = clientRect.height / scale.y;
  if (domElement) {
    const win = getWindow(domElement);
    const offsetWin = offsetParent && isElement(offsetParent) ? getWindow(offsetParent) : offsetParent;
    let currentWin = win;
    let currentIFrame = getFrameElement(currentWin);
    while (currentIFrame && offsetParent && offsetWin !== currentWin) {
      const iframeScale = getScale(currentIFrame);
      const iframeRect = currentIFrame.getBoundingClientRect();
      const css = getComputedStyle$1(currentIFrame);
      const left = iframeRect.left + (currentIFrame.clientLeft + parseFloat(css.paddingLeft)) * iframeScale.x;
      const top = iframeRect.top + (currentIFrame.clientTop + parseFloat(css.paddingTop)) * iframeScale.y;
      x *= iframeScale.x;
      y *= iframeScale.y;
      width *= iframeScale.x;
      height *= iframeScale.y;
      x += left;
      y += top;
      currentWin = getWindow(currentIFrame);
      currentIFrame = getFrameElement(currentWin);
    }
  }
  return rectToClientRect({
    width,
    height,
    x,
    y
  });
}

// If <html> has a CSS width greater than the viewport, then this will be
// incorrect for RTL.
function getWindowScrollBarX(element, rect) {
  const leftScroll = getNodeScroll(element).scrollLeft;
  if (!rect) {
    return getBoundingClientRect(getDocumentElement(element)).left + leftScroll;
  }
  return rect.left + leftScroll;
}

function getHTMLOffset(documentElement, scroll) {
  const htmlRect = documentElement.getBoundingClientRect();
  const x = htmlRect.left + scroll.scrollLeft - getWindowScrollBarX(documentElement, htmlRect);
  const y = htmlRect.top + scroll.scrollTop;
  return {
    x,
    y
  };
}

function convertOffsetParentRelativeRectToViewportRelativeRect(_ref) {
  let {
    elements,
    rect,
    offsetParent,
    strategy
  } = _ref;
  const isFixed = strategy === 'fixed';
  const documentElement = getDocumentElement(offsetParent);
  const topLayer = elements ? isTopLayer(elements.floating) : false;
  if (offsetParent === documentElement || topLayer && isFixed) {
    return rect;
  }
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  let scale = createCoords(1);
  const offsets = createCoords(0);
  const isOffsetParentAnElement = isHTMLElement(offsetParent);
  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isOffsetParentAnElement) {
      const offsetRect = getBoundingClientRect(offsetParent);
      scale = getScale(offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    }
  }
  const htmlOffset = documentElement && !isOffsetParentAnElement && !isFixed ? getHTMLOffset(documentElement, scroll) : createCoords(0);
  return {
    width: rect.width * scale.x,
    height: rect.height * scale.y,
    x: rect.x * scale.x - scroll.scrollLeft * scale.x + offsets.x + htmlOffset.x,
    y: rect.y * scale.y - scroll.scrollTop * scale.y + offsets.y + htmlOffset.y
  };
}

function getClientRects(element) {
  return Array.from(element.getClientRects());
}

// Gets the entire size of the scrollable document area, even extending outside
// of the `<html>` and `<body>` rect bounds if horizontally scrollable.
function getDocumentRect(element) {
  const html = getDocumentElement(element);
  const scroll = getNodeScroll(element);
  const body = element.ownerDocument.body;
  const width = max(html.scrollWidth, html.clientWidth, body.scrollWidth, body.clientWidth);
  const height = max(html.scrollHeight, html.clientHeight, body.scrollHeight, body.clientHeight);
  let x = -scroll.scrollLeft + getWindowScrollBarX(element);
  const y = -scroll.scrollTop;
  if (getComputedStyle$1(body).direction === 'rtl') {
    x += max(html.clientWidth, body.clientWidth) - width;
  }
  return {
    width,
    height,
    x,
    y
  };
}

// Safety check: ensure the scrollbar space is reasonable in case this
// calculation is affected by unusual styles.
// Most scrollbars leave 15-18px of space.
const SCROLLBAR_MAX = 25;
function getViewportRect(element, strategy) {
  const win = getWindow(element);
  const html = getDocumentElement(element);
  const visualViewport = win.visualViewport;
  let width = html.clientWidth;
  let height = html.clientHeight;
  let x = 0;
  let y = 0;
  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height;
    const visualViewportBased = isWebKit();
    if (!visualViewportBased || visualViewportBased && strategy === 'fixed') {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }
  const windowScrollbarX = getWindowScrollBarX(html);
  // <html> `overflow: hidden` + `scrollbar-gutter: stable` reduces the
  // visual width of the <html> but this is not considered in the size
  // of `html.clientWidth`.
  if (windowScrollbarX <= 0) {
    const doc = html.ownerDocument;
    const body = doc.body;
    const bodyStyles = getComputedStyle(body);
    const bodyMarginInline = doc.compatMode === 'CSS1Compat' ? parseFloat(bodyStyles.marginLeft) + parseFloat(bodyStyles.marginRight) || 0 : 0;
    const clippingStableScrollbarWidth = Math.abs(html.clientWidth - body.clientWidth - bodyMarginInline);
    if (clippingStableScrollbarWidth <= SCROLLBAR_MAX) {
      width -= clippingStableScrollbarWidth;
    }
  } else if (windowScrollbarX <= SCROLLBAR_MAX) {
    // If the <body> scrollbar is on the left, the width needs to be extended
    // by the scrollbar amount so there isn't extra space on the right.
    width += windowScrollbarX;
  }
  return {
    width,
    height,
    x,
    y
  };
}

// Returns the inner client rect, subtracting scrollbars if present.
function getInnerBoundingClientRect(element, strategy) {
  const clientRect = getBoundingClientRect(element, true, strategy === 'fixed');
  const top = clientRect.top + element.clientTop;
  const left = clientRect.left + element.clientLeft;
  const scale = isHTMLElement(element) ? getScale(element) : createCoords(1);
  const width = element.clientWidth * scale.x;
  const height = element.clientHeight * scale.y;
  const x = left * scale.x;
  const y = top * scale.y;
  return {
    width,
    height,
    x,
    y
  };
}
function getClientRectFromClippingAncestor(element, clippingAncestor, strategy) {
  let rect;
  if (clippingAncestor === 'viewport') {
    rect = getViewportRect(element, strategy);
  } else if (clippingAncestor === 'document') {
    rect = getDocumentRect(getDocumentElement(element));
  } else if (isElement(clippingAncestor)) {
    rect = getInnerBoundingClientRect(clippingAncestor, strategy);
  } else {
    const visualOffsets = getVisualOffsets(element);
    rect = {
      x: clippingAncestor.x - visualOffsets.x,
      y: clippingAncestor.y - visualOffsets.y,
      width: clippingAncestor.width,
      height: clippingAncestor.height
    };
  }
  return rectToClientRect(rect);
}
function hasFixedPositionAncestor(element, stopNode) {
  const parentNode = getParentNode(element);
  if (parentNode === stopNode || !isElement(parentNode) || isLastTraversableNode(parentNode)) {
    return false;
  }
  return getComputedStyle$1(parentNode).position === 'fixed' || hasFixedPositionAncestor(parentNode, stopNode);
}

// A "clipping ancestor" is an `overflow` element with the characteristic of
// clipping (or hiding) child elements. This returns all clipping ancestors
// of the given element up the tree.
function getClippingElementAncestors(element, cache) {
  const cachedResult = cache.get(element);
  if (cachedResult) {
    return cachedResult;
  }
  let result = getOverflowAncestors(element, [], false).filter(el => isElement(el) && getNodeName(el) !== 'body');
  let currentContainingBlockComputedStyle = null;
  const elementIsFixed = getComputedStyle$1(element).position === 'fixed';
  let currentNode = elementIsFixed ? getParentNode(element) : element;

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  while (isElement(currentNode) && !isLastTraversableNode(currentNode)) {
    const computedStyle = getComputedStyle$1(currentNode);
    const currentNodeIsContaining = isContainingBlock(currentNode);
    if (!currentNodeIsContaining && computedStyle.position === 'fixed') {
      currentContainingBlockComputedStyle = null;
    }
    const shouldDropCurrentNode = elementIsFixed ? !currentNodeIsContaining && !currentContainingBlockComputedStyle : !currentNodeIsContaining && computedStyle.position === 'static' && !!currentContainingBlockComputedStyle && (currentContainingBlockComputedStyle.position === 'absolute' || currentContainingBlockComputedStyle.position === 'fixed') || isOverflowElement(currentNode) && !currentNodeIsContaining && hasFixedPositionAncestor(element, currentNode);
    if (shouldDropCurrentNode) {
      // Drop non-containing blocks.
      result = result.filter(ancestor => ancestor !== currentNode);
    } else {
      // Record last containing block for next iteration.
      currentContainingBlockComputedStyle = computedStyle;
    }
    currentNode = getParentNode(currentNode);
  }
  cache.set(element, result);
  return result;
}

// Gets the maximum area that the element is visible in due to any number of
// clipping ancestors.
function getClippingRect(_ref) {
  let {
    element,
    boundary,
    rootBoundary,
    strategy
  } = _ref;
  const elementClippingAncestors = boundary === 'clippingAncestors' ? isTopLayer(element) ? [] : getClippingElementAncestors(element, this._c) : [].concat(boundary);
  const clippingAncestors = [...elementClippingAncestors, rootBoundary];
  const firstRect = getClientRectFromClippingAncestor(element, clippingAncestors[0], strategy);
  let top = firstRect.top;
  let right = firstRect.right;
  let bottom = firstRect.bottom;
  let left = firstRect.left;
  for (let i = 1; i < clippingAncestors.length; i++) {
    const rect = getClientRectFromClippingAncestor(element, clippingAncestors[i], strategy);
    top = max(rect.top, top);
    right = min(rect.right, right);
    bottom = min(rect.bottom, bottom);
    left = max(rect.left, left);
  }
  return {
    width: right - left,
    height: bottom - top,
    x: left,
    y: top
  };
}

function getDimensions(element) {
  const {
    width,
    height
  } = getCssDimensions(element);
  return {
    width,
    height
  };
}

function getRectRelativeToOffsetParent(element, offsetParent, strategy) {
  const isOffsetParentAnElement = isHTMLElement(offsetParent);
  const documentElement = getDocumentElement(offsetParent);
  const isFixed = strategy === 'fixed';
  const rect = getBoundingClientRect(element, true, isFixed, offsetParent);
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  const offsets = createCoords(0);

  // If the <body> scrollbar appears on the left (e.g. RTL systems). Use
  // Firefox with layout.scrollbar.side = 3 in about:config to test this.
  function setLeftRTLScrollbarOffset() {
    offsets.x = getWindowScrollBarX(documentElement);
  }
  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isOffsetParentAnElement) {
      const offsetRect = getBoundingClientRect(offsetParent, true, isFixed, offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    } else if (documentElement) {
      setLeftRTLScrollbarOffset();
    }
  }
  if (isFixed && !isOffsetParentAnElement && documentElement) {
    setLeftRTLScrollbarOffset();
  }
  const htmlOffset = documentElement && !isOffsetParentAnElement && !isFixed ? getHTMLOffset(documentElement, scroll) : createCoords(0);
  const x = rect.left + scroll.scrollLeft - offsets.x - htmlOffset.x;
  const y = rect.top + scroll.scrollTop - offsets.y - htmlOffset.y;
  return {
    x,
    y,
    width: rect.width,
    height: rect.height
  };
}

function isStaticPositioned(element) {
  return getComputedStyle$1(element).position === 'static';
}

function getTrueOffsetParent(element, polyfill) {
  if (!isHTMLElement(element) || getComputedStyle$1(element).position === 'fixed') {
    return null;
  }
  if (polyfill) {
    return polyfill(element);
  }
  let rawOffsetParent = element.offsetParent;

  // Firefox returns the <html> element as the offsetParent if it's non-static,
  // while Chrome and Safari return the <body> element. The <body> element must
  // be used to perform the correct calculations even if the <html> element is
  // non-static.
  if (getDocumentElement(element) === rawOffsetParent) {
    rawOffsetParent = rawOffsetParent.ownerDocument.body;
  }
  return rawOffsetParent;
}

// Gets the closest ancestor positioned element. Handles some edge cases,
// such as table ancestors and cross browser bugs.
function getOffsetParent(element, polyfill) {
  const win = getWindow(element);
  if (isTopLayer(element)) {
    return win;
  }
  if (!isHTMLElement(element)) {
    let svgOffsetParent = getParentNode(element);
    while (svgOffsetParent && !isLastTraversableNode(svgOffsetParent)) {
      if (isElement(svgOffsetParent) && !isStaticPositioned(svgOffsetParent)) {
        return svgOffsetParent;
      }
      svgOffsetParent = getParentNode(svgOffsetParent);
    }
    return win;
  }
  let offsetParent = getTrueOffsetParent(element, polyfill);
  while (offsetParent && isTableElement(offsetParent) && isStaticPositioned(offsetParent)) {
    offsetParent = getTrueOffsetParent(offsetParent, polyfill);
  }
  if (offsetParent && isLastTraversableNode(offsetParent) && isStaticPositioned(offsetParent) && !isContainingBlock(offsetParent)) {
    return win;
  }
  return offsetParent || getContainingBlock(element) || win;
}

const getElementRects = async function (data) {
  const getOffsetParentFn = this.getOffsetParent || getOffsetParent;
  const getDimensionsFn = this.getDimensions;
  const floatingDimensions = await getDimensionsFn(data.floating);
  return {
    reference: getRectRelativeToOffsetParent(data.reference, await getOffsetParentFn(data.floating), data.strategy),
    floating: {
      x: 0,
      y: 0,
      width: floatingDimensions.width,
      height: floatingDimensions.height
    }
  };
};

function isRTL(element) {
  return getComputedStyle$1(element).direction === 'rtl';
}

const platform = {
  convertOffsetParentRelativeRectToViewportRelativeRect,
  getDocumentElement,
  getClippingRect,
  getOffsetParent,
  getElementRects,
  getClientRects,
  getDimensions,
  getScale,
  isElement,
  isRTL
};

function rectsAreEqual(a, b) {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

// https://samthor.au/2021/observing-dom/
function observeMove(element, onMove) {
  let io = null;
  let timeoutId;
  const root = getDocumentElement(element);
  function cleanup() {
    var _io;
    clearTimeout(timeoutId);
    (_io = io) == null || _io.disconnect();
    io = null;
  }
  function refresh(skip, threshold) {
    if (skip === void 0) {
      skip = false;
    }
    if (threshold === void 0) {
      threshold = 1;
    }
    cleanup();
    const elementRectForRootMargin = element.getBoundingClientRect();
    const {
      left,
      top,
      width,
      height
    } = elementRectForRootMargin;
    if (!skip) {
      onMove();
    }
    if (!width || !height) {
      return;
    }
    const insetTop = floor(top);
    const insetRight = floor(root.clientWidth - (left + width));
    const insetBottom = floor(root.clientHeight - (top + height));
    const insetLeft = floor(left);
    const rootMargin = -insetTop + "px " + -insetRight + "px " + -insetBottom + "px " + -insetLeft + "px";
    const options = {
      rootMargin,
      threshold: max(0, min(1, threshold)) || 1
    };
    let isFirstUpdate = true;
    function handleObserve(entries) {
      const ratio = entries[0].intersectionRatio;
      if (ratio !== threshold) {
        if (!isFirstUpdate) {
          return refresh();
        }
        if (!ratio) {
          // If the reference is clipped, the ratio is 0. Throttle the refresh
          // to prevent an infinite loop of updates.
          timeoutId = setTimeout(() => {
            refresh(false, 1e-7);
          }, 1000);
        } else {
          refresh(false, ratio);
        }
      }
      if (ratio === 1 && !rectsAreEqual(elementRectForRootMargin, element.getBoundingClientRect())) {
        // It's possible that even though the ratio is reported as 1, the
        // element is not actually fully within the IntersectionObserver's root
        // area anymore. This can happen under performance constraints. This may
        // be a bug in the browser's IntersectionObserver implementation. To
        // work around this, we compare the element's bounding rect now with
        // what it was at the time we created the IntersectionObserver. If they
        // are not equal then the element moved, so we refresh.
        refresh();
      }
      isFirstUpdate = false;
    }

    // Older browsers don't support a `document` as the root and will throw an
    // error.
    try {
      io = new IntersectionObserver(handleObserve, {
        ...options,
        // Handle <iframe>s
        root: root.ownerDocument
      });
    } catch (_e) {
      io = new IntersectionObserver(handleObserve, options);
    }
    io.observe(element);
  }
  refresh(true);
  return cleanup;
}

/**
 * Automatically updates the position of the floating element when necessary.
 * Should only be called when the floating element is mounted on the DOM or
 * visible on the screen.
 * @returns cleanup function that should be invoked when the floating element is
 * removed from the DOM or hidden from the screen.
 * @see https://floating-ui.com/docs/autoUpdate
 */
function autoUpdate(reference, floating, update, options) {
  if (options === void 0) {
    options = {};
  }
  const {
    ancestorScroll = true,
    ancestorResize = true,
    elementResize = typeof ResizeObserver === 'function',
    layoutShift = typeof IntersectionObserver === 'function',
    animationFrame = false
  } = options;
  const referenceEl = unwrapElement(reference);
  const ancestors = ancestorScroll || ancestorResize ? [...(referenceEl ? getOverflowAncestors(referenceEl) : []), ...(floating ? getOverflowAncestors(floating) : [])] : [];
  ancestors.forEach(ancestor => {
    ancestorScroll && ancestor.addEventListener('scroll', update, {
      passive: true
    });
    ancestorResize && ancestor.addEventListener('resize', update);
  });
  const cleanupIo = referenceEl && layoutShift ? observeMove(referenceEl, update) : null;
  let reobserveFrame = -1;
  let resizeObserver = null;
  if (elementResize) {
    resizeObserver = new ResizeObserver(_ref => {
      let [firstEntry] = _ref;
      if (firstEntry && firstEntry.target === referenceEl && resizeObserver && floating) {
        // Prevent update loops when using the `size` middleware.
        // https://github.com/floating-ui/floating-ui/issues/1740
        resizeObserver.unobserve(floating);
        cancelAnimationFrame(reobserveFrame);
        reobserveFrame = requestAnimationFrame(() => {
          var _resizeObserver;
          (_resizeObserver = resizeObserver) == null || _resizeObserver.observe(floating);
        });
      }
      update();
    });
    if (referenceEl && !animationFrame) {
      resizeObserver.observe(referenceEl);
    }
    if (floating) {
      resizeObserver.observe(floating);
    }
  }
  let frameId;
  let prevRefRect = animationFrame ? getBoundingClientRect(reference) : null;
  if (animationFrame) {
    frameLoop();
  }
  function frameLoop() {
    const nextRefRect = getBoundingClientRect(reference);
    if (prevRefRect && !rectsAreEqual(prevRefRect, nextRefRect)) {
      update();
    }
    prevRefRect = nextRefRect;
    frameId = requestAnimationFrame(frameLoop);
  }
  update();
  return () => {
    var _resizeObserver2;
    ancestors.forEach(ancestor => {
      ancestorScroll && ancestor.removeEventListener('scroll', update);
      ancestorResize && ancestor.removeEventListener('resize', update);
    });
    cleanupIo == null || cleanupIo();
    (_resizeObserver2 = resizeObserver) == null || _resizeObserver2.disconnect();
    resizeObserver = null;
    if (animationFrame) {
      cancelAnimationFrame(frameId);
    }
  };
}

/**
 * Modifies the placement by translating the floating element along the
 * specified axes.
 * A number (shorthand for `mainAxis` or distance), or an axes configuration
 * object may be passed.
 * @see https://floating-ui.com/docs/offset
 */
const offset = offset$1;

/**
 * Optimizes the visibility of the floating element by shifting it in order to
 * keep it in view when it will overflow the clipping boundary.
 * @see https://floating-ui.com/docs/shift
 */
const shift = shift$1;

/**
 * Optimizes the visibility of the floating element by flipping the `placement`
 * in order to keep it in view when the preferred placement(s) will overflow the
 * clipping boundary. Alternative to `autoPlacement`.
 * @see https://floating-ui.com/docs/flip
 */
const flip = flip$1;

/**
 * Provides data to position an inner element of the floating element so that it
 * appears centered to the reference element.
 * @see https://floating-ui.com/docs/arrow
 */
const arrow = arrow$1;

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a given reference element.
 */
const computePosition = (reference, floating, options) => {
  // This caches the expensive `getClippingElementAncestors` function so that
  // multiple lifecycle resets re-use the same result. It only lives for a
  // single call. If other functions become expensive, we can add them as well.
  const cache = new Map();
  const mergedOptions = {
    platform,
    ...options
  };
  const platformWithCache = {
    ...mergedOptions.platform,
    _c: cache
  };
  return computePosition$1(reference, floating, {
    ...mergedOptions,
    platform: platformWithCache
  });
};

var styles = css`:host {
  --bsi-dropdown-background: var(--bsi-color-background-inverse);
}`;

let ItPopover = class ItPopover extends BaseComponent {
    constructor() {
        super(...arguments);
        this.open = false;
        this.controlled = false;
        this.placement = 'bottom-start';
        this.offset = 12;
        this.noFlip = false;
        /** Centre the arrow on the trigger instead of the fixed notch position. Used by avatar dropdowns. */
        this.centerArrow = false;
        this._onDocumentClick = (event) => {
            if (!this.open)
                return;
            const path = event.composedPath();
            if (!path.includes(this) && !path.includes(this._contentElement) && !path.includes(this._triggerElement)) {
                this.open = false;
            }
        };
    }
    get _triggerElement() {
        return this._triggerSlot?.assignedElements({ flatten: true })[0];
    }
    get _contentElement() {
        return this._contentSlot?.assignedElements({ flatten: true })[0];
    }
    _setChildrenProperties() {
        if (this._triggerElement && this._triggerElement.tagName === 'IT-BUTTON') {
            if (this._triggerElement.disabled) {
                this._triggerElement.removeAttribute('it-aria-haspopup');
                this._triggerElement.expanded = undefined;
                return;
            }
            if (!this._triggerElement.hasAttribute('it-aria-haspopup')) {
                this._triggerElement?.setAttribute('it-aria-haspopup', 'true');
            }
            this._triggerElement.expanded = this.open;
        }
        else {
            if (this._triggerElement?.hasAttribute('disabled')) {
                this._triggerElement.removeAttribute('aria-haspopup');
                this._triggerElement.removeAttribute('aria-expanded');
                return;
            }
            if (!this._triggerElement?.hasAttribute('aria-haspopup')) {
                this._triggerElement?.setAttribute('aria-haspopup', 'true');
            }
            this._triggerElement?.setAttribute('aria-expanded', String(this.open));
        }
    }
    setupStandardEvents() {
        document.addEventListener('click', this._onDocumentClick);
        this._triggerElement?.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggle();
        });
        this._triggerElement?.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.open) {
                this.closePopover();
                this._triggerElement?.focus();
            }
        });
        this._contentElement?.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.open) {
                this.closePopover();
                this._triggerElement?.focus();
            }
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback?.();
        document.removeEventListener('click', this._onDocumentClick);
        this._cleanup?.();
    }
    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        if (!this.controlled)
            this.setupStandardEvents();
    }
    updated(changedProps) {
        this._setChildrenProperties();
        if (changedProps.has('open')) {
            if (this.open) {
                this._show();
                this.dispatchEvent(new CustomEvent('it-popover-open', { bubbles: true, composed: true }));
            }
            else {
                this._hide();
                this.dispatchEvent(new CustomEvent('it-popover-close', { bubbles: true, composed: true }));
            }
        }
    }
    _createArrow() {
        if (!this._contentElement)
            return;
        if (!this._arrowElement) {
            this._arrowElement = document.createElement('div');
            this._arrowElement.className = 'arrow';
            this._arrowElement.part = 'popover-arrow';
            this._contentElement.prepend(this._arrowElement);
        }
    }
    _show() {
        if (!this._triggerElement || !this._contentElement)
            return;
        this._contentElement.style.position = 'absolute';
        this._contentElement.style.visibility = 'visible';
        this._createArrow();
        this._cleanup = autoUpdate(this._triggerElement, this._contentElement, () => {
            computePosition(this._triggerElement, this._contentElement, {
                placement: this.placement,
                middleware: [
                    offset({
                        mainAxis: this.offset,
                        crossAxis: this.crossAxisOffset,
                    }),
                    flip({ mainAxis: !this.noFlip, crossAxis: !this.noFlip }),
                    shift({ padding: 8 }),
                    // Commentato perchè nella toolbar con dropdown che ha position absolute, vogliamo il min-width impostato da css con var(--<prefix>dropdown-min-width)
                    // size({
                    //   apply({ rects, elements }) {
                    //     Object.assign(elements.floating.style, {
                    //       minWidth: `${rects.reference.width}px`,
                    //     });
                    //   },
                    // }),
                    arrow({ element: this._arrowElement }),
                ],
            }).then(({ x, y, placement, middlewareData }) => {
                Object.assign(this._contentElement.style, {
                    left: `${x}px`,
                    top: `${y}px`,
                });
                if (middlewareData.arrow) {
                    const { x: arrowX, y: arrowY } = middlewareData.arrow;
                    const placementSide = placement.split('-')[0];
                    const staticSide = {
                        top: 'bottom',
                        right: 'left',
                        bottom: 'top',
                        left: 'right',
                    }[placementSide];
                    const triggerRect = this._triggerElement.getBoundingClientRect();
                    const contentRect = this._contentElement.getBoundingClientRect();
                    const triggerCenter = triggerRect.left + triggerRect.width / 2;
                    const arrowLeft = triggerCenter - contentRect.left - this._triggerElement.offsetWidth / 2 + 20;
                    // When `centerArrow` is set (e.g. avatar dropdowns), centre the arrow
                    // on the trigger using Floating UI's computed offset. Otherwise keep
                    // the fixed notch position relied on by standard dropdowns.
                    const left = this.centerArrow ? arrowX : arrowLeft + (this.crossAxisOffset ?? 0);
                    const isLeftOrRight = ['left', 'right'].includes(placementSide);
                    const additionalOffsetTop = isLeftOrRight ? (this.crossAxisOffset ?? 0) : 0;
                    Object.assign(this._arrowElement.style, {
                        left: arrowX != null ? `${left}px` : '',
                        top: arrowY != null ? `${arrowY + additionalOffsetTop}px` : '',
                        right: '',
                        bottom: '',
                        [staticSide]: `-8px`,
                        position: 'absolute',
                        transform: 'rotate(45deg)',
                    });
                }
            });
        });
        this._focusContent();
    }
    _hide() {
        this._cleanup?.();
        this._contentElement.style.visibility = 'hidden';
    }
    _focusContent() {
        this._contentElement?.focus?.();
    }
    toggle() {
        this.open = !this.open;
    }
    openPopover() {
        this.open = true;
    }
    closePopover() {
        this.open = false;
    }
    render() {
        return html `
      <slot name="trigger" part="trigger" @slotchange=${this._setChildrenProperties}></slot>
      <slot name="content" part="popover-content"></slot>
    `;
    }
};
ItPopover.styles = styles;
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItPopover.prototype, "open", void 0);
__decorate([
    property({ type: Boolean }),
    __metadata("design:type", Object)
], ItPopover.prototype, "controlled", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], ItPopover.prototype, "placement", void 0);
__decorate([
    property({ type: Number }),
    __metadata("design:type", Number)
], ItPopover.prototype, "offset", void 0);
__decorate([
    property({ type: Number, attribute: 'cross-axis-offset' }),
    __metadata("design:type", Number)
], ItPopover.prototype, "crossAxisOffset", void 0);
__decorate([
    property({ type: Boolean, attribute: 'no-flip' }),
    __metadata("design:type", Boolean)
], ItPopover.prototype, "noFlip", void 0);
__decorate([
    property({ type: Boolean, attribute: 'center-arrow' }),
    __metadata("design:type", Boolean)
], ItPopover.prototype, "centerArrow", void 0);
__decorate([
    query('slot[name="trigger"]'),
    __metadata("design:type", HTMLSlotElement)
], ItPopover.prototype, "_triggerSlot", void 0);
__decorate([
    query('slot[name="content"]'),
    __metadata("design:type", HTMLSlotElement)
], ItPopover.prototype, "_contentSlot", void 0);
ItPopover = __decorate([
    customElement('it-popover')
], ItPopover);

export { ItPopover };
//# sourceMappingURL=it-popover.js.map
